import React, { useState } from "react";
import {
  AutoComplete,
  Button,
  Card,
  Col,
  Input,
  InputNumber,
  Popconfirm,
  Row,
  Select,
  Space,
  Tag,
  Typography
} from "antd";
import {
  CloudOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  TeamOutlined
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { aggregateHours, normalizeStaff, sectionText } from "./calc";
import DictationField from "./DictationField";
import { formatHours } from "./format";

const number = value => (value === null || value === undefined || value === "" ? null : value);

// ---------------------------------------------------------------------------------------------
// Weather
// ---------------------------------------------------------------------------------------------

const ERRORS = {
  NO_LOCATION: "The site address of the project is missing or unknown.",
  TOO_FAR_AHEAD: "No weather is available for this day.",
  NO_DATA: "No weather is available for this day."
};

export const WeatherCard = ({ weather, codes, editable, onChange, onRefresh, refreshing }) => {
  const { t } = useTranslation("dashboard");
  // The fetched weather is usually right: the fields open on demand (or when it is missing)
  const [open, setOpen] = useState(false);
  const w = weather || {};
  const set = patch => onChange({ ...w, ...patch });
  const summary = [
    w.conditions,
    w.temperatureMorning !== null && w.temperatureMorning !== undefined
      ? `${w.temperatureMorning} °C ${t("morning")}`
      : "",
    w.temperatureNoon !== null && w.temperatureNoon !== undefined
      ? `${w.temperatureNoon} °C ${t("noon")}`
      : "",
    w.wind ? `${t("Wind")}: ${w.wind}${w.windKmh !== null ? ` (${w.windKmh} km/h)` : ""}` : "",
    w.precipitationMm !== null && w.precipitationMm !== undefined
      ? `${t("Precipitation")}: ${w.precipitationMm} mm`
      : ""
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <Card
      className="sdi-card"
      size="small"
      title={
        <Space size={6}>
          <CloudOutlined /> {t("Weather")}
          {w.source === "AUTO" && <Tag>{w.provisional ? t("Forecast") : t("Open-Meteo")}</Tag>}
          {w.source === "MANUAL" && <Tag color="blue">{t("Entered by hand")}</Tag>}
        </Space>
      }
      extra={
        editable && (
          <Space size={4}>
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => setOpen(value => !value)}
              aria-label={t("Edit the weather")}
            />
            <Button
              size="small"
              icon={<ReloadOutlined />}
              loading={refreshing}
              onClick={onRefresh}
              aria-label={t("Fetch the weather again")}
            />
          </Space>
        )
      }
    >
      {w.error && w.source === "NONE" && (
        <Typography.Paragraph type="warning" className="sdi-small">
          {t(
            ERRORS[w.error] ||
              "The weather service could not be reached. Please enter the weather by hand."
          )}
        </Typography.Paragraph>
      )}
      {!editable || !(open || !summary) ? (
        <Typography.Text>{summary || "–"}</Typography.Text>
      ) : (
        <Row gutter={[12, 12]}>
          <Col xs={12} md={6}>
            <label className="sdi-label">{t("Morning °C")}</label>
            <InputNumber
              className="sdi-full"
              inputMode="decimal"
              step={0.5}
              value={w.temperatureMorning}
              onChange={value => set({ temperatureMorning: number(value) })}
            />
          </Col>
          <Col xs={12} md={6}>
            <label className="sdi-label">{t("Noon °C")}</label>
            <InputNumber
              className="sdi-full"
              inputMode="decimal"
              step={0.5}
              value={w.temperatureNoon}
              onChange={value => set({ temperatureNoon: number(value) })}
            />
          </Col>
          <Col xs={24} md={12}>
            <label className="sdi-label">{t("Conditions")}</label>
            <Select
              className="sdi-full"
              showSearch
              allowClear
              optionFilterProp="label"
              value={w.code ?? undefined}
              onChange={value => {
                const code = value ?? null;
                set({ code, conditions: codes.find(c => c.code === code)?.text || "" });
              }}
              options={codes.map(c => ({ value: c.code, label: c.text }))}
            />
          </Col>
          <Col xs={12} md={6}>
            <label className="sdi-label">{t("Wind km/h")}</label>
            <InputNumber
              className="sdi-full"
              inputMode="decimal"
              min={0}
              value={w.windKmh}
              onChange={value => set({ windKmh: number(value), wind: "" })}
            />
          </Col>
          <Col xs={12} md={6}>
            <label className="sdi-label">{t("Precipitation mm")}</label>
            <InputNumber
              className="sdi-full"
              inputMode="decimal"
              min={0}
              step={0.1}
              value={w.precipitationMm}
              onChange={value => set({ precipitationMm: number(value) })}
            />
          </Col>
          <Col xs={24} md={12}>
            <label className="sdi-label">{t("Note on the weather")}</label>
            <Input
              value={w.note}
              maxLength={300}
              placeholder={t("e.g. frost in the morning, no concreting")}
              onChange={event => set({ note: event.target.value })}
            />
          </Col>
        </Row>
      )}
    </Card>
  );
};

// ---------------------------------------------------------------------------------------------
// Staff
// ---------------------------------------------------------------------------------------------

export const StaffCard = ({ staff, employees, editable, onChange, onAttendance, loading }) => {
  const { t } = useTranslation("dashboard");
  const rows = staff || [];
  const hours = aggregateHours(rows);
  // Typed times stay as typed until the field is left ("7" → "07:00"); totals use the
  // normalized rows.
  const update = (index, patch) =>
    onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  const tidy = index => onChange(rows.map((row, i) => (i === index ? normalizeStaff(row) : row)));
  const remove = index => onChange(rows.filter((_, i) => i !== index));
  const add = () => onChange([...rows, { name: "", start: "", end: "", minutes: 0 }]);
  const options = employees.map(e => ({ value: e.name, employee: e._id }));

  return (
    <Card
      className="sdi-card"
      size="small"
      title={
        <Space size={6}>
          <TeamOutlined /> {t("Staff on site")}
          <Tag>
            {hours.people} · {formatHours(hours.minutes)} {t("h")}
          </Tag>
        </Space>
      }
      extra={
        editable && (
          <Button size="small" loading={loading} onClick={onAttendance}>
            {t("From attendance")}
          </Button>
        )
      }
    >
      {!rows.length && (
        <Typography.Paragraph type="secondary">
          {t("Nobody booked time on this project for the day yet.")}
        </Typography.Paragraph>
      )}
      {rows.map((row, index) => (
        <div key={row._id || index} className="sdi-row">
          <Row gutter={[8, 8]} align="bottom">
            <Col xs={24} md={8}>
              {editable ? (
                <AutoComplete
                  className="sdi-full"
                  value={row.name}
                  options={options}
                  placeholder={t("Name")}
                  filterOption={(input, option) =>
                    option.value.toLowerCase().includes(input.toLowerCase())
                  }
                  onChange={(value, option) =>
                    update(index, { name: value, employee: option?.employee || null })
                  }
                />
              ) : (
                <Typography.Text strong>{row.name}</Typography.Text>
              )}
              {row.source === "ATTENDANCE" && <Tag className="sdi-source">{t("Attendance")}</Tag>}
            </Col>
            <Col xs={8} md={3}>
              <Input
                aria-label={t("from")}
                placeholder={t("from")}
                inputMode="numeric"
                disabled={!editable}
                value={row.start}
                onChange={event => update(index, { start: event.target.value })}
                onBlur={() => tidy(index)}
              />
            </Col>
            <Col xs={8} md={3}>
              <Input
                aria-label={t("to")}
                placeholder={t("to")}
                inputMode="numeric"
                disabled={!editable}
                value={row.end}
                onChange={event => update(index, { end: event.target.value })}
                onBlur={() => tidy(index)}
              />
            </Col>
            <Col xs={8} md={3}>
              <InputNumber
                className="sdi-full"
                aria-label={t("Break (min)")}
                placeholder={t("Break")}
                min={0}
                disabled={!editable}
                value={row.breakMinutes || null}
                onChange={value => update(index, { breakMinutes: value || 0 })}
              />
            </Col>
            <Col xs={12} md={4}>
              <InputNumber
                className="sdi-full"
                aria-label={t("Hours")}
                addonAfter={t("h")}
                min={0}
                max={24}
                step={0.25}
                disabled={
                  !editable || Boolean(normalizeStaff(row).start && normalizeStaff(row).end)
                }
                value={Math.round((normalizeStaff(row).minutes / 60) * 100) / 100}
                onChange={value => update(index, { minutes: Math.round((value || 0) * 60) })}
              />
            </Col>
            <Col xs={12} md={3} className="sdi-row-actions">
              {editable && (
                <Button
                  danger
                  type="text"
                  icon={<DeleteOutlined />}
                  aria-label={t("Remove")}
                  onClick={() => remove(index)}
                />
              )}
            </Col>
          </Row>
        </div>
      ))}
      {editable && (
        <Button icon={<PlusOutlined />} onClick={add} className="sdi-add">
          {t("Add person")}
        </Button>
      )}
    </Card>
  );
};

// ---------------------------------------------------------------------------------------------
// One author's contribution
// ---------------------------------------------------------------------------------------------

export const SECTION_LABELS = {
  workPerformed: "Work performed",
  materialsDelivered: "Materials delivered",
  instructions: "Instructions by the client / architect",
  specialEvents: "Special events",
  visitors: "Visitors"
};

const PLACEHOLDERS = {
  workPerformed: "What was done today? e.g. formwork for the floor slab set up, axis A–C.",
  materialsDelivered: "e.g. 12 m³ concrete C25/30, 2 pallets of bricks",
  instructions: "Who ordered what? e.g. architect: additional expansion joint in axis 3",
  specialEvents: "Accidents, damage, inspections, delays",
  visitors: "e.g. client, architect, building inspector"
};

/** Rows of a small list (subcontractors, equipment) with free columns. */
const ListRows = ({ rows, columns, editable, onChange, addLabel }) => {
  const { t } = useTranslation("dashboard");
  const update = (index, patch) =>
    onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  if (!editable && !rows.length) return null;
  return (
    <>
      {rows.map((row, index) => (
        <Row gutter={[8, 8]} key={index} className="sdi-row" align="middle">
          {columns.map(column => (
            <Col xs={column.xs} md={column.md} key={column.key}>
              {column.number ? (
                <InputNumber
                  className="sdi-full"
                  min={0}
                  precision={0}
                  placeholder={t(column.label)}
                  aria-label={t(column.label)}
                  disabled={!editable}
                  value={row[column.key] || null}
                  onChange={value => update(index, { [column.key]: value || 0 })}
                />
              ) : (
                <Input
                  placeholder={t(column.label)}
                  aria-label={t(column.label)}
                  disabled={!editable}
                  value={row[column.key]}
                  onChange={event => update(index, { [column.key]: event.target.value })}
                />
              )}
            </Col>
          ))}
          {editable && (
            <Col xs={4} md={2}>
              <Button
                danger
                type="text"
                icon={<DeleteOutlined />}
                aria-label={t("Remove")}
                onClick={() => onChange(rows.filter((_, i) => i !== index))}
              />
            </Col>
          )}
        </Row>
      ))}
      {editable && (
        <Button
          size="small"
          icon={<PlusOutlined />}
          className="sdi-add"
          onClick={() => onChange([...rows, {}])}
        >
          {t(addLabel)}
        </Button>
      )}
    </>
  );
};

export const ContributionCard = ({
  title,
  contribution,
  editable,
  onChange,
  voiceConfigured,
  voiceTranslateTo,
  extra
}) => {
  const { t } = useTranslation("dashboard");
  const c = contribution || {};
  const set = patch => onChange({ ...c, ...patch });
  return (
    <Card className="sdi-card" size="small" title={title} extra={extra}>
      {Object.keys(SECTION_LABELS).map(key =>
        editable || c[key] ? (
          <div key={key} className="sdi-section">
            <label className="sdi-label">{t(SECTION_LABELS[key])}</label>
            {editable ? (
              <DictationField
                value={c[key] || ""}
                onChange={value => set({ [key]: value })}
                placeholder={t(PLACEHOLDERS[key])}
                voiceConfigured={voiceConfigured}
                voiceTranslateTo={voiceTranslateTo}
                minRows={key === "workPerformed" ? 4 : 2}
              />
            ) : (
              <div className="sdi-text">{c[key]}</div>
            )}
          </div>
        ) : null
      )}
      <div className="sdi-section">
        <label className="sdi-label">{t("Subcontractors")}</label>
        <ListRows
          rows={c.subcontractors || []}
          editable={editable}
          onChange={rows => set({ subcontractors: rows })}
          addLabel="Add subcontractor"
          columns={[
            { key: "company", label: "Company", xs: 20, md: 8 },
            { key: "trade", label: "Trade", xs: 12, md: 6 },
            { key: "workers", label: "Workers", number: true, xs: 8, md: 3 },
            { key: "note", label: "Note", xs: 24, md: 5 }
          ]}
        />
      </div>
      <div className="sdi-section">
        <label className="sdi-label">{t("Equipment")}</label>
        <ListRows
          rows={c.equipment || []}
          editable={editable}
          onChange={rows => set({ equipment: rows })}
          addLabel="Add equipment"
          columns={[
            { key: "name", label: "Equipment", xs: 14, md: 10 },
            { key: "quantity", label: "Quantity", number: true, xs: 6, md: 4 },
            { key: "note", label: "Note", xs: 24, md: 8 }
          ]}
        />
      </div>
    </Card>
  );
};

/** What the other authors of the day wrote (read only), merged per section. */
export const OthersCard = ({ merged, exclude }) => {
  const { t } = useTranslation("dashboard");
  const others = key =>
    (merged?.sections?.[key] || []).filter(part => String(part.author) !== String(exclude));
  const keys = Object.keys(SECTION_LABELS).filter(key => others(key).length);
  if (!keys.length) return null;
  return (
    <Card className="sdi-card sdi-card--others" size="small" title={t("Written by others today")}>
      {keys.map(key => (
        <div key={key} className="sdi-section">
          <label className="sdi-label">{t(SECTION_LABELS[key])}</label>
          <div className="sdi-text">{sectionText(others(key))}</div>
          {others(key).length === 1 && <div className="sdi-small">{others(key)[0].authorName}</div>}
        </div>
      ))}
    </Card>
  );
};

/** Delete with confirmation, used in the header. */
export const DeleteButton = ({ onConfirm }) => {
  const { t } = useTranslation("dashboard");
  return (
    <Popconfirm title={t("Delete this diary entry?")} onConfirm={onConfirm}>
      <Button danger icon={<DeleteOutlined />} aria-label={t("Delete")} />
    </Popconfirm>
  );
};
